/** Step router — renders the correct step component by type with animated slide transitions. */
import { useState, useRef, useEffect, type ReactNode } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { useSession, SessionContext } from '../SessionContext';
import { MCQStep } from './MCQStep';
import { TrueFalseStep } from './TrueFalseStep';
import { TextStep } from './TextStep';
import { ClozeStep } from './ClozeStep';
import { ClozeDiagramStep } from './ClozeDiagramStep';
import { RankingStep } from './RankingStep';
import { ClassifyStep } from './ClassifyStep';
import { ImageHotspotStep } from './ImageHotspotStep';
import { ClozeImageStep } from './ClozeImageStep';
import { NumericStep } from './NumericStep';
import { InfoStep } from './InfoStep';

const SLIDE_DISTANCE = 40;

/**
 * Freezes the session context for a step during exit animations.
 *
 * Tracks the currentIndex this component was created with. Once the
 * session moves to a different step, stops updating the provided context
 * value so exiting step components never see the new step's state.
 * This prevents shortcut badge flicker (and other visual glitches)
 * during AnimatePresence exit transitions.
 */
function StepSessionFreeze({ children }: { children: ReactNode }) {
  const liveValue = useSession();
  const [frozenValue, setFrozenValue] = useState(liveValue);
  const [trackedIndex] = useState(liveValue.state.currentIndex);

  // Derived state update during render (React-approved pattern).
  // Only update while on the same step. Once the session advances,
  // frozenValue retains the last state for THIS step (phase: 'graded').
  if (liveValue.state.currentIndex === trackedIndex && liveValue !== frozenValue) {
    setFrozenValue(liveValue);
  }

  return <SessionContext.Provider value={frozenValue}>{children}</SessionContext.Provider>;
}

/**
 * Routes to the correct step component based on questionType.
 * Wraps transitions in AnimatePresence with direction-aware animations:
 * forward navigation slides content left, backward slides right.
 */
export function StepRouter() {
  const { currentStep, state } = useSession();

  // Track previous index in a ref to derive slide direction without setState-during-render.
  // The ref is updated via useEffect after each committed render, so during the render
  // where currentIndex changes, prevIndexRef still holds the old value — giving the
  // correct direction for AnimatePresence.
  const prevIndexRef = useRef(state.currentIndex);

  const direction = state.currentIndex >= prevIndexRef.current ? 1 : -1;
  useEffect(() => {
    prevIndexRef.current = state.currentIndex;
  }, [state.currentIndex]);

  if (!currentStep) return null;

  function renderStep() {
    switch (currentStep?.questionType) {
      case 'mcq':
        return <MCQStep />;
      case 'true_false':
        return <TrueFalseStep />;
      case 'text':
        return <TextStep />;
      case 'cloze':
        return <ClozeStep />;
      case 'cloze_diagram':
        return <ClozeDiagramStep />;
      case 'cloze_image':
        return <ClozeImageStep />;
      case 'ranking':
        return <RankingStep />;
      case 'classify':
        return <ClassifyStep />;
      case 'image_hotspot':
        return <ImageHotspotStep />;
      case 'numeric':
        return <NumericStep />;
      default:
        return <InfoStep />;
    }
  }

  const variants = {
    enter: (d: number) => ({ x: d * SLIDE_DISTANCE, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (d: number) => ({ x: d * -SLIDE_DISTANCE, opacity: 0 }),
  };

  return (
    <AnimatePresence mode="wait" initial={false} custom={direction}>
      <motion.div
        key={state.currentIndex}
        custom={direction}
        className="flex min-h-0 flex-1 flex-col"
        variants={variants}
        initial="enter"
        animate="center"
        exit="exit"
        transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
      >
        <StepSessionFreeze>{renderStep()}</StepSessionFreeze>
      </motion.div>
    </AnimatePresence>
  );
}
